import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";

export default function Testimonials() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items]);

  if (!items.length) return null;
  const item = items[idx];
  const next = () => setIdx((i) => (i + 1) % items.length);
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);

  return (
    <section className="py-32 lg:py-40 bg-[#0a0a0c] border-y border-hair" data-testid="home-testimonials">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300 mb-12">
            <span className="block w-8 h-px bg-rouge" /><span>{t.testimonials.eyebrow}</span>
          </div>
        </Reveal>

        <div className="relative min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl"
              data-testid="testimonial-quote"
            >
              <span className="font-serif-editorial text-rouge text-7xl leading-none block mb-4" aria-hidden>“</span>
              <p className="font-serif-editorial italic text-3xl sm:text-4xl lg:text-5xl text-ink-100 leading-[1.15] tracking-tight">
                {lang === "fr" ? item.quote_fr : item.quote}
              </p>
              <footer className="mt-10 flex items-center gap-4">
                <div className="w-10 h-px bg-rouge" />
                <div>
                  <div className="text-ink-100 text-sm tracking-wide">{item.author}</div>
                  <div className="text-ink-500 text-[11px] tracking-[0.22em] uppercase mt-1">
                    {lang === "fr" ? item.role_fr : item.role}
                  </div>
                </div>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-16 flex items-center justify-between">
          <div className="text-ink-500 text-[11px] tracking-[0.24em] uppercase">
            {String(idx + 1).padStart(2, "0")} <span className="text-ink-500/50">/ {String(items.length).padStart(2, "0")}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="previous testimonial"
              data-testid="testimonial-prev"
              data-cursor="PREV"
              className="w-12 h-12 border border-hair hover:border-ink-100 text-ink-300 hover:text-ink-100 flex items-center justify-center transition-colors"
            ><ChevronLeft size={18} /></button>
            <button
              onClick={next}
              aria-label="next testimonial"
              data-testid="testimonial-next"
              data-cursor="NEXT"
              className="w-12 h-12 border border-hair hover:border-ink-100 text-ink-300 hover:text-ink-100 flex items-center justify-center transition-colors"
            ><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
